import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { UserCircle, Loader2 } from "lucide-react";

type ProfileFormData = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  display_on_contact: boolean;
  whatsapp_contact_url: string;
  whatsapp_booking_url: string;
};

const ProfilePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  
  const { register, handleSubmit, setValue, control, reset } = useForm<ProfileFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      display_on_contact: false,
      whatsapp_contact_url: '',
      whatsapp_booking_url: '',
    }
  });

  // Fetch profile data
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Get profile image URL
  const imageUrl = profile?.profile_image 
    ? supabase.storage.from('profile_images').getPublicUrl(profile.profile_image).data.publicUrl
    : null;

  // Update form when profile data is loaded
  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        phone: profile.phone || '',
        email: user?.email || '',
        display_on_contact: profile.display_on_contact || false,
        whatsapp_contact_url: profile.whatsapp_contact_url || '',
        whatsapp_booking_url: profile.whatsapp_booking_url || '',
      });
    }
  }, [profile, user?.email, reset]);

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setIsLoading(true);
      
      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          display_on_contact: data.display_on_contact,
          whatsapp_contact_url: data.whatsapp_contact_url,
          whatsapp_booking_url: data.whatsapp_booking_url,
        })
        .eq('id', user?.id);

      if (profileError) throw profileError;

      // Update email if changed
      if (data.email !== user?.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: data.email,
        });
        if (emailError) throw emailError;
      }

      // Invalidate queries to refresh data across the app (footer, contact page, etc.)
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['contact-admins'] });

      toast.success("Profil mis à jour avec succès");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsLoading(true);
      const fileExt = file.name.split('.').pop();
      const filePath = `${user?.id}/${crypto.randomUUID()}.${fileExt}`;

      // Upload image
      const { error: uploadError } = await supabase.storage
        .from('profile_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Update profile with new image
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_image: filePath })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['contact-admins'] });

      toast.success("Photo de profil mise à jour");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user?.email || '', {
        redirectTo: `${window.location.origin}/auth/update-password`,
      });
      
      if (error) throw error;
      
      toast.success("Instructions envoyées par email");
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  if (!user) {
    navigate(`/auth?redirect=${encodeURIComponent('/profile')}`, { replace: true });
    return null;
  }

  if (profileLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container mx-auto py-8 px-4 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto py-8 px-4">
        <h1 className="text-3xl font-playfair text-villa-blue mb-8">Mon Profil</h1>
        
        <div className="grid gap-8 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Photo de profil</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <Avatar className="w-32 h-32">
                  <AvatarImage src={imageUrl || undefined} />
                  <AvatarFallback>
                    <UserCircle className="w-16 h-16" />
                  </AvatarFallback>
                </Avatar>
                
                <div>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={isLoading}
                    className="hidden"
                    id="profile-image"
                  />
                  <Button
                    asChild
                    variant="outline"
                    disabled={isLoading}
                  >
                    <label htmlFor="profile-image">
                      Changer la photo
                    </label>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Informations personnelles</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prénom</Label>
                    <Input
                      id="first_name"
                      {...register('first_name')}
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nom</Label>
                    <Input
                      id="last_name"
                      {...register('last_name')}
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      {...register('phone')}
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      disabled={isLoading}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Controller
                      name="display_on_contact"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          id="display_on_contact"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isLoading}
                        />
                      )}
                    />
                    <Label htmlFor="display_on_contact">Afficher mes informations sur la page contact</Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp_contact_url">Lien WhatsApp (Contact et Pied de page)</Label>
                    <Input
                      id="whatsapp_contact_url"
                      placeholder="https://wa.me/33600000000?text=Bonjour..."
                      {...register('whatsapp_contact_url')}
                      disabled={isLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp_booking_url">Lien WhatsApp (Pop-up Réservation)</Label>
                    <Input
                      id="whatsapp_booking_url"
                      placeholder="https://wa.me/33600000000?text=Bonjour..."
                      {...register('whatsapp_booking_url')}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      "Enregistrer les modifications"
                    )}
                  </Button>
                  
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handlePasswordReset}
                    disabled={isLoading}
                  >
                    Modifier le mot de passe
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
